import { css, FlattenSimpleInterpolation } from 'styled-components';

import { CommonTheme, ManualCSSTheme } from 'app/shared/theme';
import { generateCSSPropForScreenSize } from 'app/shared/components/styleUtils/generateCSSPropForScreenSize';

export interface WithFlexProps {
  flex?: number | string | number[] | string[];
  flexDirection?: string | string[];
  flexWrap?: string | string[];
  flexFlow?: string | string[];
  justifyContent?: string | string[];
  alignItems?: string | string[];
  alignContent?: string | string[];
  gap?: number | number[];
  rowGap?: number | number[];
  columnGap?: number | number[];
}

interface WithFlexCSSProps extends WithFlexProps {
  theme: CommonTheme | ManualCSSTheme;
}

function generateCssProperty(
  theme: CommonTheme | ManualCSSTheme,
  propName: string,
  value: string | number
) {
  const propMapping: { [key: string]: string } = {
    flex: 'flex',
    flexDirection: 'flex-direction',
    flexWrap: 'flex-wrap',
    flexFlow: 'flex-flow',
    justifyContent: 'justify-content',
    alignItems: 'align-items',
    alignContent: 'align-content',
    gap: 'gap',
    rowGap: 'row-gap',
    columnGap: 'column-gap',
  };

  if (typeof value === 'number') {
    return `${propMapping[propName]}: ${theme.ruler[value]}px;`;
  }

  return `${propMapping[propName]}: ${value};`;
}

const generateCSSForScreenSize = (
  screenSize: string,
  { theme, ...props }: WithFlexCSSProps
) => {
  let s = '';

  Object.keys(props).forEach((propName) => {
    s += generateCSSPropForScreenSize({
      theme,
      screenSize,
      propName,
      value: props[propName as keyof WithFlexProps],
      generateCssProperty,
    });
  });

  return css`${s}`;
};

const generateFlex = (props: WithFlexCSSProps) => {
  const { theme } = props;
  let styles: (string | FlattenSimpleInterpolation)[] = [];

  Object.keys(theme.media).forEach((breakPointName) => {
    const generatedCSS = generateCSSForScreenSize(breakPointName, props);

    if (generatedCSS.length > 0) {
      const mediaQuery = theme.utils.getMediaQuery(breakPointName);
      styles.push(mediaQuery`${generatedCSS}`);
    }
  });

  return styles;
};

export const withFlexCSS = ({
  theme,
  flex,
  flexDirection,
  flexWrap,
  flexFlow,
  justifyContent,
  alignItems,
  alignContent,
  gap,
  rowGap,
  columnGap,
}: WithFlexCSSProps) => css`
  ${flex && generateFlex({ theme, flex })}

  ${
    (flexDirection ||
      flexWrap ||
      flexFlow ||
      justifyContent ||
      alignItems ||
      alignContent ||
      gap ||
      rowGap ||
      columnGap) &&
    css`
      display: flex;

      ${generateFlex({
        theme,
        flexDirection,
        flexWrap,
        flexFlow,
        justifyContent,
        alignItems,
        alignContent,
        gap,
        rowGap,
        columnGap,
      })}
    `
  }
`;
